import { useMemo } from 'react'

import type { HandleCallbackActionParams } from '@domain-group/javascript-utils'

import {
  DashboardModule, SortableList, Table, Text,
} from '@domain-group/suit-theme'

import TaskIcon from '@components/TaskIcon'

import { splitTaskAddress } from '@functions/orderItemTask'

import useIdSort from '@hooks/useIdSort'
import useSelector from '@hooks/useSelector'

import type { OrderItemModel, OrderItemTaskModel, UserModel } from '@models/types'

import DraggableOrderItemTask from '../DraggableOrderItemTask'
import OrderItemTaskActions from '../OrderItemTaskActions'

const buildColumns = (orderItems: OrderItemModel[], users: UserModel[]) => [
  {
    dataKey: 'order_item_task_type_id',
    cellProps: {
      width: 40,
    },
    render: (order_item_task_type_id: number) => <TaskIcon taskTypeId={order_item_task_type_id} />,
  },
  {
    title: 'Order',
    dataKey: 'order_item_id',
    cellProps: {
      style: { paddingLeft: 0 },
      verticalAlign: 'middle',
      width: 250,
    },
    render: (order_item_id: number) => {
      const orderItem = orderItems[order_item_id]
      const { options_string, quantity, title } = orderItem || {}

      return (
        <Text variant="ellipsis" width={250}>
          {quantity ? `${quantity} x ` : ''}{title ? `${title}${options_string}` : 'Custom Task'}
        </Text>
      )
    },
  },
  {
    title: 'Suburb',
    dataKey: 'address',
    cellProps: {
      verticalAlign: 'middle',
      width: 110,
    },
    render: (address: string) => {
      const { suburb } = splitTaskAddress(address)
      return <Text variant="ellipsis" width={110}>{suburb}</Text>
    },
  },
  {
    title: 'Address',
    dataKey: 'address',
    cellProps: {
      key: 'address-2',
      verticalAlign: 'middle',
      width: 170,
    },
    render: (address: string) => {
      const { address: addressStr } = splitTaskAddress(address)
      return <Text variant="ellipsis" width={170}>{addressStr}</Text>
    },
  },
  {
    title: 'Instructions',
    dataKey: 'instructions',
    cellProps: {
      verticalAlign: 'middle',
      minWidth: 280,
    },
    render: (instructions: string) => <Text variant="ellipsis" width={280}>{instructions || 'N/A'}</Text>,
  },
  {
    title: 'Assignee',
    dataKey: 'provider_employee_id',
    cellProps: {
      verticalAlign: 'middle',
      width: 200,
    },
    render: (provider_employee_id: string) => {
      // @ts-expect-error TS2367
      const user = Object.values(users).find(user => user.actable_id === provider_employee_id) || {}

      // @ts-expect-error TS2339
      return <Text variant="ellipsis">{user.id ? `${user.first_name} ${user.last_name}` : 'Unknown User'}</Text>
    },
  },
  {
    title: 'Actions',
    dataKey: 'id',
    cellProps: {
      textAlign: 'center',
    },
    // @ts-expect-error TS7006
    render: (_, orderItemTask: OrderItemTaskModel) => <OrderItemTaskActions orderItemTask={orderItemTask} />,
  },
]

interface OrderItemTaskGroupProps {
  callbacks: {
    updateSortOrder: (payload: HandleCallbackActionParams) => Promise<object>,
  },
  groupDate: string,
  orderItemTaskIds: number[],
}

const OrderItemTaskGroup = (props: OrderItemTaskGroupProps) => {
  const {
    callbacks: {
      updateSortOrder: updateSort,
    },
    groupDate,
    orderItemTaskIds,
  } = props

  const { orderItems, users } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2345
  const memoColumns = useMemo(() => buildColumns(orderItems, users), [orderItemTaskIds.length])

  // @ts-expect-error TS2322
  const sortPayload = useIdSort({ callbacks: { updateSort }, idsArray: orderItemTaskIds })
  const {
    callbacks: { modifySort },
    sortedIds,
  } = sortPayload

  return (
    <DashboardModule title={groupDate}>
      <SortableList
        callbacks={{
          onDragEnd: (fromIndex, toIndex) => modifySort(fromIndex, toIndex!),
        }}
        // @ts-expect-error TS2322
        droppableId="Group"
      >
        <Table
          boxProps={{ border: 'none', maxHeight: 800, overflowY: 'auto' }}
          // @ts-expect-error TS2322
          columns={memoColumns}
          stickyHeader
        >
          <SortableList.Dropzone droppableId={`Group-${groupDate}`}>
            <tbody>
              {sortedIds.map((orderItemTaskId, index) => (
                <SortableList.Item
                  key={`OrderItemTask-${orderItemTaskId}`}
                  draggableId={`OrderItemTask-${orderItemTaskId}`}
                  index={index}
                >
                  {/*
                   // @ts-expect-error TS2322 */}
                  <DraggableOrderItemTask columns={memoColumns} orderItemTaskId={orderItemTaskId} />
                </SortableList.Item>
              ))}
            </tbody>
          </SortableList.Dropzone>
        </Table>
      </SortableList>
    </DashboardModule>
  )
}

export default OrderItemTaskGroup
